import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import TextInput from "view/pages/MyProfile/Components/Inputs/TextInput";
import { useDispatch } from "react-redux";
import apiLibrary from "services/api";
import { addTagSchema } from "utils/validationSchemas";
import { Toasts } from "view/components/Toasts";
import Button from "view/components/Button";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { closeAddOrEditTagModalAction } from "store/modals/reducer.actions";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import XCloseIcon from "assets/icons/HeroIcons/XCloseIcon";
import { fetchTagsAction } from "store/tagging";

const style: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#ffff",
  borderRadius: "10px",
};

/**
 * Component for adding, editing, or using a form as a template.
 * @returns {JSX.Element} The component's JSX representation
 */
export const AddOrEditTagModal = () => {
  // State variables

  const { addOrEditTagModal } = useSelector((state: RootState) => state.modals);

  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const [formInitialValues, setFormInitialValues] = useState<any>({
    name: "",
    description: "",
  });

  // Initialize formInitialValues when in editMode or templateMode
  useEffect(() => {
    if (addOrEditTagModal.editMode) {
      const { name, description } = addOrEditTagModal?.data;
      setFormInitialValues({
        name: name,
        description: description,
      });
    }
  }, [addOrEditTagModal?.data, addOrEditTagModal.editMode]);

  /**
   * Handle form submission.
   * @param {object} values - Form values
   * @param {Function} setSubmitting - Function to set submitting state
   * @example
   * handleSubmit({ name: "Form Name" }, { setSubmitting: (bool) => {} });
   */
  const handleSubmit = async (
    values: any,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const formValues = {
        ...values,
      };

      if (addOrEditTagModal.editMode && addOrEditTagModal.data.id) {
        // Edit an existing form
        const { data, message } = await apiLibrary.Tagging.editTag(
          addOrEditTagModal.data.id,
          formValues
        );

        Toasts.success(message);
        handleClose();
        dispatch(fetchTagsAction());
      } else {
        // Add a new form
        const { data, message } = await apiLibrary.Tagging.addTag(formValues);
        Toasts.success(message);
        handleClose();
        dispatch(fetchTagsAction());
      }
    } catch (error: any) {
      // Handle API errors
      const errorMsg = error?.response?.data?.message ?? error.message;
      Toasts.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle modal closure.
   * @example
   * handleClose();
   */
  const handleClose = () => {
    dispatch(closeAddOrEditTagModalAction());
    setFormInitialValues({
      name: "",
      description: "",
    });
  };

  return (
    <Modal
      open={addOrEditTagModal.isOpen}
      onClose={handleClose}
   aria-labelledby="modal-modal-title"
      disableAutoFocus={true}
      aria-describedby="modal-modal-description"
      className="border-none"
    >
      <Box sx={style} className="dark:bg-secondaryLight ">
        <Formik
          initialValues={formInitialValues}
          validationSchema={addTagSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form>
              <div className="flex flex-col justify-start items-start w-[80vw] max-w-[700px]  max-h-[700px] overflow-y-auto rounded-lg">
                <div
                  className="relative flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-4 p-6 rounded-lg dark:bg-secondaryLight bg-bgWhite"
                  style={{
                    boxShadow:
                      "0px 2px 8px 0 rgba(2,13,36,0.14), 0px 2px 24px 0 rgba(2,13,36,0.08)",
                  }}
                >
                  <div className="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 py-0.5">
                    <div className="relative flex items-center self-stretch justify-start flex-grow-0 flex-shrink-0">
                      <p className="flex-grow w-[608px] text-xl font-semibold text-left text-textMid capitalize dark:text-textMain">
                        {addOrEditTagModal.editMode ? "Edit tag" : "Add tag"}
                      </p>
                      <button title="close" onClick={handleClose} type="button">
                        <XCloseIcon
                          width={24}
                          height={24}
                          viewBox="0 0 24 24"
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 dark:bg-secondaryLight">
                    <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-2">
                      <TextInput
                        label="Tag name *"
                        type="text"
                        name="name"
                        setFieldValue={setFieldValue}
                        error={errors?.name}
                        touched={touched.name}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        placeholder="Tag Name"
                        value={values.name}
                      />
                      {!(errors?.name && touched?.name) && (
                        <p className="flex-grow w-[608px] text-xs text-left mt-[-18px] text-textMidLight capitalize">
                          60 max characters
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col items-start self-stretch justify-start flex-grow-0 flex-shrink-0 gap-2">
                      <TextInput
                        label="Description *"
                        type="text"
                        name="description"
                        setFieldValue={setFieldValue}
                        error={errors?.description}
                        touched={touched.description}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        placeholder=" Add description to guide on the intent/planned use, etc"
                        rows={4}
                        fieldAs="textarea"
                        value={values.description}
                      />
                      {!(errors?.description && touched?.description) && (
                        <p className="flex-grow w-[608px] text-xs text-left mt-[-15px] text-textMidLight capitalize">
                          6000 max characters
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center self-stretch justify-end flex-grow-0 flex-shrink-0 gap-2">
                    <Button
                      type="button"
                      text="Cancel"
                      filledColor="primary"
                      outlinedColor="primary"
                      textColor="textWhite"
                      className="w-24 h-11"
                      width="35"
                      height="13"
                      fontStyle="font-semibold"
                      variant="outlined"
                      onClick={handleClose}
                    />

                    <Button
                      disabled={isSubmitting}
                      type="submit"
                      text="Save"
                      filledColor="primary"
                      outlinedColor="primary"
                      textColor="textWhite"
                      className="w-24 h-11"
                      width="35"
                      height="13"
                      fontStyle="font-semibold"
                      variant="filled"
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
